import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { handleApiError } from "../utils/apiHelpers";
import driverService from "../api/services/driverService";
import { fetchCollectionData } from "../api/services/firestore";

export const suspendDriverService = createAsyncThunk(
  "service/suspendDriver",
  async (data, thunkAPI) => {
    try {
      const response = await driverService.suspendDriver(data);
      if (response && response.data) {
        return response.data;
      }
      throw new Error("No data found");
    } catch (error) {
      handleApiError(error);
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const enableDriverService = createAsyncThunk(
  "service/enableDriver",
  async (data, thunkAPI) => {
    try {
      const response = await driverService.enableDriver(data);
      if (response && response.data) {
        return response.data;
      }
      throw new Error("No data found");
    } catch (error) {
      handleApiError(error);
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteDriverService = createAsyncThunk(
  "service/deleteDriver",
  async (data, thunkAPI) => {
    try {
      const response = await driverService.deleteDriver(data);
      if (response && response.data) {
        return response.data;
      }
      throw new Error("No data found");
    } catch (error) {
      handleApiError(error);
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const acceptDriverService = createAsyncThunk(
  "service/acceptDriver",
  async (data, thunkAPI) => {
    try {
      const response = await driverService.acceptOnboardingDriver(data);
      if (response && response.data) {
        return response.data;
      }
      throw new Error("No data found");
    } catch (error) {
      handleApiError(error);
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const rejectDriverService = createAsyncThunk(
  "service/rejectDriver",
  async (data, thunkAPI) => {
    try {
      const response = await driverService.rejectOnboardingDriver(data);
      if (response && response.data) {
        return response.data;
      }
      throw new Error("No data found");
    } catch (error) {
      handleApiError(error);
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const driversWeeklyPaymentsService = createAsyncThunk(
  "service/driverWeeklyPayments",
  async (data, thunkAPI) => {
    try {
      const response = await driverService.fetchDriverWeeklyPayments(data);
      if (response && response.data) {
        return response.data;
      }
      throw new Error("No data found");
    } catch (error) {
      handleApiError(error);
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const shiftScheduleService = createAsyncThunk(
  "service/driverShiftSchedule",
  async (data, thunkAPI) => {
    try {
      const response = await driverService.updateShiftSchedule(data);
      if (response && response.data) {
        return response.data;
      }
      throw new Error("No data found");
    } catch (error) {
      handleApiError(error);
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updatePointsService = createAsyncThunk(
  "service/updatePoints",
  async (data, thunkAPI) => {
    try {
      const response = await driverService.updatePoints(data);
      if (response && response.data) {
        return response.data;
      }
      throw new Error("No data found");
    } catch (error) {
      handleApiError(error);
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateDriverService = createAsyncThunk(
  "service/updateDriver",
  async (data, thunkAPI) => {
    try {
      const response = await driverService.updateDriver(data);
      if (response && response.data) {
        return response.data;
      }
      throw new Error("No data found");
    } catch (error) {
      handleApiError(error);
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// export const getDriverCoordinates = createAsyncThunk(
//   "service/getDriverCoordinates",
//   async (data, thunkAPI) => {
//     try {
//       const response = await fetchCollectionData(data);
//       return response;
//     } catch (error) {
//       handleApiError(error);
//       return thunkAPI.rejectWithValue(error.response?.data || error.message);
//     }
//   }
// );

export const getDriverCoordinates = (collectionName) => (dispatch) => {
  dispatch(driverSlice.actions.clearUnsubscribe());
  dispatch(driverSlice.actions.setLoading());

  const unsubscribe = fetchCollectionData(
    collectionName,
    (data) => {
      dispatch(driverSlice.actions.updateDriverCoordinates(data));
    },
    (error) => dispatch(driverSlice.actions.setError(error))
  );

  dispatch(driverSlice.actions.setUnsubscribe(unsubscribe));
};

const driverSlice = createSlice({
  name: "drivers",
  initialState: {
    driverDetailData: {},
    selectedDriverOrder: [],
    onboardingDrivers: {},
    weeklyPayments: {},
    driverCoordinates: [],
    unsubscribe: null,
    loading: false,
    error: null,
  },

  reducers: {
    updateDriverData: (state, action) => {
      state.driverDetailData = action.payload;
    },
    selectDriverOrders: (state, action) => {
      state.selectedDriverOrder = action.payload;
    },
    updateOnboardingDrivers: (state, action) => {
      state.onboardingDrivers = action.payload;
    },
    updateDriverCoordinates: (state, action) => {
      state.driverCoordinates = action.payload;
    },
    setLoading: (state) => {
      state.loading = true;
    },
    setUnsubscribe: (state, action) => {
      state.unsubscribe = action.payload;
    },
    clearUnsubscribe: (state) => {
      if (typeof state.unsubscribe === "function") {
        state.unsubscribe();
      }
      state.unsubscribe = null;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(suspendDriverService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(suspendDriverService.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
      })
      .addCase(suspendDriverService.rejected, (state, action) => {
        state.error = null;
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(enableDriverService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(enableDriverService.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
      })
      .addCase(enableDriverService.rejected, (state, action) => {
        state.error = null;
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(deleteDriverService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDriverService.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
      })
      .addCase(deleteDriverService.rejected, (state, action) => {
        state.error = null;
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(acceptDriverService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(acceptDriverService.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
      })
      .addCase(acceptDriverService.rejected, (state, action) => {
        state.error = null;
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(rejectDriverService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rejectDriverService.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
      })
      .addCase(rejectDriverService.rejected, (state, action) => {
        state.error = null;
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(driversWeeklyPaymentsService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(driversWeeklyPaymentsService.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
      })
      .addCase(driversWeeklyPaymentsService.rejected, (state, action) => {
        state.error = null;
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(shiftScheduleService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(shiftScheduleService.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
      })
      .addCase(shiftScheduleService.rejected, (state, action) => {
        state.error = null;
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(updatePointsService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePointsService.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
      })
      .addCase(updatePointsService.rejected, (state, action) => {
        state.error = null;
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(updateDriverService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDriverService.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
      })
      .addCase(updateDriverService.rejected, (state, action) => {
        state.error = null;
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  updateDriverData,
  selectDriverOrders,
  updateOnboardingDrivers,
  updateDriverCoordinates,
  setLoading,
  setError,
  setUnsubscribe,
  clearUnsubscribe,
} = driverSlice.actions;

export default driverSlice.reducer;
